import React from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  TextField
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSectionStyles } from '@/styles/utilities/hooks';
import { DraggableDialog } from '../../../shared/components/DraggableDialog';

/**
 * Dialog component for displaying and editing FMO cluster details
 * Shows cluster name, description, mode, and residues visualization
 */
const FMOClusterDetailsDialog = ({
  open,
  onClose,
  clusterDetails,
  clusterNewName,
  clusterNewDescription,
  onNameChange,
  onDescriptionChange,
  onUpdateCluster,
  clusterspecs,
  currentComparisonTarget,
  seenRowsByTarget,
  onSeenRowsUpdate
}) => {
  const theme = useTheme();
  const sectionStyles = useSectionStyles();

  // Handle dialog close and reset
  const handleClose = () => {
    onClose();
    // Reset comparison tracking when dialog is closed
    if (onSeenRowsUpdate) {
      onSeenRowsUpdate({});
    }
  };

  // Check if save button should be disabled
  const isSaveDisabled = () => {
    if (!clusterDetails) return true;
    const nameChanged = clusterNewName.trim() !== (clusterDetails.name || '');
    const descriptionChanged = clusterNewDescription.trim() !== (clusterDetails.description || '');
    return !nameChanged && !descriptionChanged;
  };

  return (
    <DraggableDialog
      open={open}
      onClose={handleClose}
      title="Residue Set Details"
      variant="details"
    >
      {clusterDetails && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1.5), mb: theme.spacing(1) }}>
            {/* Residue Set Name Field */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
              <Typography variant="caption" sx={sectionStyles.sectionHeader}>
                RESIDUE SET NAME
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={clusterNewName}
                onChange={(e) => onNameChange(e.target.value)}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: theme.typography.fontSize.base
                  }
                }}
              />
            </Box>

            {/* Description Field */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
              <Typography variant="caption" sx={sectionStyles.sectionHeader}>
                DESCRIPTION
              </Typography>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={3}
                value={clusterNewDescription}
                onChange={(e) => onDescriptionChange(e.target.value)}
                placeholder="Enter description..."
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: theme.typography.fontSize.base
                  }
                }}
              />
            </Box>
            
            {/* Save button and mode chip in same row */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mt: theme.spacing(0.5),
              minHeight: theme.spacing(3.5) // Dynamic height based on density
            }}>
              {/* Mode chip */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: theme.spacing(1),
                height: '100%'
              }}>
                {clusterDetails.mode === 'auto' ? (
                  <>
                    <Chip 
                      label="AUTO" 
                      size="small" 
                      color="primary" 
                    />
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontSize: theme.typography.fontSize.sm, 
                        color: theme.palette.text.secondary,
                        display: 'flex',
                        alignItems: 'center',
                        height: '100%'
                      }}
                    >
                      {(() => {
                        const clusterspec = clusterspecs.find(spec => spec.id_ === clusterDetails.clusterspec_id);
                        if (clusterspec) {
                          return clusterspec.name;
                        }
                        return 'Auto-generated';
                      })()}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Chip 
                      label="MANUAL" 
                      size="small" 
                      color="secondary" 
                    />
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontSize: theme.typography.fontSize.sm, 
                        color: theme.palette.text.secondary,
                        display: 'flex',
                        alignItems: 'center',
                        height: '100%'
                      }}
                    >
                      User-defined
                    </Typography>
                  </>
                )}
              </Box>
              
              {/* Save button */}
              <Button 
                onClick={onUpdateCluster} 
                variant="contained"
                size="medium"
                disabled={isSaveDisabled()}
                sx={{ 
                  fontSize: theme.typography.fontSize.base,
                  px: theme.spacing(2),
                  minWidth: 'auto'
                }}
              >
                Save Changes
              </Button>
            </Box>
          </Box>
          
          {/* Residues Display */}
          {clusterDetails.residues && clusterDetails.residues.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, mt: theme.spacing(1), gap: theme.spacing(1) }}>
              <Typography variant="caption" sx={sectionStyles.sectionHeader}>
                RESIDUES ({clusterDetails.residues.length})
              </Typography>
              <Box sx={{ 
                flex: 1,
                overflow: 'auto', 
                border: '1px solid',
                borderColor: theme.palette.divider,
                borderRadius: theme.spacing(0.5),
                p: theme.spacing(1.5),
                backgroundColor: theme.palette.background.paper,
                '&::-webkit-scrollbar': {
                  width: theme.spacing(1),
                  height: theme.spacing(1)
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: theme.palette.grey[100]
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: theme.palette.grey[400],
                  borderRadius: theme.spacing(0.5),
                  '&:hover': {
                    backgroundColor: theme.palette.grey[500]
                  }
                }
              }}>
                <ResiduesVisualization 
                  residues={clusterDetails.residues}
                  currentComparisonTarget={currentComparisonTarget}
                  seenRowsByTarget={seenRowsByTarget}
                  onSeenRowsUpdate={onSeenRowsUpdate}
                />
              </Box>
            </Box>
          )}
        </Box>
      )}
    </DraggableDialog>
  );
};

/**
 * Component for visualizing residues grouped by chain
 */
const ResiduesVisualization = ({ 
  residues, 
  currentComparisonTarget, 
  seenRowsByTarget,
  onSeenRowsUpdate 
}) => {
  const theme = useTheme();
  
  // Parse and group residues by chain
  const residuesByChain = {};
  residues.forEach(r => {
    if (typeof r === 'string') {
      const parts = r.split(':');
      const chain = parts[0] || '';
      const resName = parts[1] || '';
      const resNum = parseInt(parts[2]) || 0;
      const insertionCode = parts[3] || '';
      
      if (!residuesByChain[chain]) {
        residuesByChain[chain] = {
          regular: {},
          insertions: {}
        };
      }
      
      // Separate insertions from regular residues
      if (insertionCode) {
        if (!residuesByChain[chain].insertions[resNum]) {
          residuesByChain[chain].insertions[resNum] = [];
        }
        residuesByChain[chain].insertions[resNum].push({
          name: resName,
          number: resNum,
          insertionCode: insertionCode
        });
      } else {
        residuesByChain[chain].regular[resNum] = {
          name: resName,
          number: resNum,
          insertionCode: ''
        };
      }
    }
  });
  
  // Update seen rows for current target
  React.useEffect(() => {
    if (currentComparisonTarget && onSeenRowsUpdate) {
      const newSeenRows = new Set(seenRowsByTarget[currentComparisonTarget] || []);
      Object.values(residuesByChain).forEach(data => {
        const regularResidues = data.regular;
        const residueNumbers = Object.keys(regularResidues).map(n => parseInt(n));
        if (residueNumbers.length > 0) {
          residueNumbers.forEach(resNum => {
            const rowStart = Math.floor((resNum - 1) / 10) * 10 + 1;
            newSeenRows.add(rowStart);
          });
        }
      });
      
      // Update state if changed
      const currentSeenRows = seenRowsByTarget[currentComparisonTarget] || [];
      if (newSeenRows.size !== currentSeenRows.length || 
          ![...newSeenRows].every(row => currentSeenRows.includes(row))) {
        onSeenRowsUpdate(prev => ({
          ...prev,
          [currentComparisonTarget]: Array.from(newSeenRows).sort((a, b) => a - b)
        }));
      }
    }
  }, [currentComparisonTarget, residuesByChain, seenRowsByTarget, onSeenRowsUpdate]);
  
  return Object.entries(residuesByChain).map(([chain, data]) => {
    const regularResidues = data.regular;
    const insertions = data.insertions;
    let allResidueNumbers = Object.keys(regularResidues).map(n => parseInt(n)).sort((a, b) => a - b);
    
    if (allResidueNumbers.length === 0) {
      // Handle chains with only insertions
      const insertionNumbers = Object.keys(insertions).map(n => parseInt(n));
      if (insertionNumbers.length > 0) {
        allResidueNumbers = insertionNumbers.sort((a, b) => a - b);
      }
    }
    
    // Group residues into rows of 10
    const rows = [];
    let lastRowEnd = 0;
    
    // Get all rows that should be shown
    const rowsToShow = new Set();
    
    // Add rows from current cluster
    const currentResidueNumbers = Object.keys(regularResidues).map(n => parseInt(n));
    if (currentResidueNumbers.length > 0) {
      currentResidueNumbers.forEach(resNum => {
        const rowStart = Math.floor((resNum - 1) / 10) * 10 + 1;
        rowsToShow.add(rowStart);
      });
    }
    
    // Add previously seen rows for this target (for comparison mode)
    if (currentComparisonTarget && seenRowsByTarget && seenRowsByTarget[currentComparisonTarget]) {
      seenRowsByTarget[currentComparisonTarget].forEach(row => rowsToShow.add(row));
    }
    
    // Convert to sorted array
    const sortedRowStarts = Array.from(rowsToShow).sort((a, b) => a - b);
    
    // Generate rows
    sortedRowStarts.forEach(start => {
      const rowResidues = [];
      
      // Always show all 10 positions in a row
      for (let i = start; i <= start + 9; i++) {
        if (regularResidues[i]) {
          rowResidues.push({ ...regularResidues[i], number: i, selected: true });
        } else {
          rowResidues.push({ number: i, selected: false });
        }
      }
      
      // Check if we need a gap indicator
      if (lastRowEnd > 0 && start > lastRowEnd + 10) {
        rows.push({ type: 'gap' });
      }
      
      rows.push({ type: 'residues', start, residues: rowResidues });
      
      // Check for insertions in this row range
      for (let i = start; i <= start + 9; i++) {
        if (insertions[i] && insertions[i].length > 0) {
          rows.push({ 
            type: 'insertions', 
            position: i, 
            residues: insertions[i].sort((a, b) => 
              a.insertionCode.localeCompare(b.insertionCode)
            )
          });
        }
      }
      
      lastRowEnd = start + 9;
    });
    
    return (
      <Box key={chain} sx={{ mb: theme.spacing(2) }}>
        <Typography variant="caption" sx={{ 
          fontWeight: theme.typography.fontWeight.medium, 
          fontSize: theme.typography.fontSize.sm, 
          display: 'block', 
          mb: theme.spacing(0.5) 
        }}>
          Chain {chain}
        </Typography>
        {rows.map((row, idx) => {
          if (row.type === 'gap') {
            return <GapIndicator key={`gap-${idx}`} />;
          }
          
          if (row.type === 'insertions') {
            return (
              <InsertionRow 
                key={`insertions-${row.position}`}
                position={row.position}
                residues={row.residues}
              />
            );
          }
          
          return (
            <ResidueRow 
              key={`row-${row.start}`}
              residues={row.residues}
            />
          );
        })}
      </Box>
    );
  });
};

/**
 * Gap indicator component
 */
const GapIndicator = () => {
  const theme = useTheme();
  
  return (
    <Box sx={{ 
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      my: theme.spacing(0.5),
      gap: theme.spacing(1)
    }}>
      <Box sx={{ 
        flex: 1,
        height: 1, 
        backgroundColor: theme.palette.text.disabled,
        opacity: 0.3
      }} />
      <Typography variant="caption" sx={{ 
        fontSize: theme.typography.fontSize.xs, 
        color: theme.palette.text.disabled,
        fontStyle: 'italic'
      }}>
        •••
      </Typography>
      <Box sx={{ 
        flex: 1,
        height: 1, 
        backgroundColor: theme.palette.text.disabled,
        opacity: 0.3
      }} />
    </Box>
  );
};

/**
 * Insertion row component
 */
const InsertionRow = ({ position, residues }) => {
  const theme = useTheme();
  
  return (
    <Box sx={{ 
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      mb: theme.spacing(0.375), // 0.3 → theme.spacing(0.375)
      ml: theme.spacing(1.5)
    }}>
      <Typography variant="caption" sx={{ 
        fontSize: theme.typography.fontSize.xs,
        color: theme.palette.text.secondary,
        fontStyle: 'italic',
        minWidth: theme.spacing(9.5) // 75px → dynamic
      }}>
        Insertions at {position}:
      </Typography>
      <Box sx={{ display: 'flex', gap: theme.spacing(0.375) }}>
        {residues.map(res => (
          <Box
            key={`${res.number}${res.insertionCode}`}
            sx={{
              width: theme.spacing(5.25), // 42px → dynamic
              height: theme.spacing(3.25), // 26px → dynamic
              border: '1px solid',
              borderColor: theme.palette.states.warningBorder,
              borderRadius: theme.spacing(0.5),
              backgroundColor: theme.palette.states.warning,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
          >
            <Typography sx={{ 
              fontSize: theme.typography.fontSize.xs, 
              fontWeight: theme.typography.fontWeight.semibold, 
              lineHeight: 1, 
              color: theme.palette.warning.dark 
            }}>
              {res.name}
            </Typography>
            <Typography sx={{ 
              fontSize: `calc(${theme.typography.fontSize.xs} * 0.9)`, 
              lineHeight: 1, 
              color: theme.palette.warning.main 
            }}>
              {res.number}{res.insertionCode}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/**
 * Regular residue row component
 */
const ResidueRow = ({ residues }) => {
  const theme = useTheme();
  
  return (
    <Box sx={{ 
      display: 'flex', 
      gap: theme.spacing(0.375), // 0.3 → dynamic
      mb: theme.spacing(0.375),
      flexWrap: 'wrap'
    }}>
      {residues.map(res => (
        <Box
          key={res.number}
          sx={{
            width: theme.spacing(5.25), // 42px → dynamic
            height: theme.spacing(3.25), // 26px → dynamic
            border: '1px solid',
            borderColor: res.selected ? theme.palette.selection.border : theme.palette.divider,
            borderRadius: theme.spacing(0.5),
            backgroundColor: res.selected ? theme.palette.selection.background : theme.palette.grey[50],
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: res.selected ? 1 : 0.3,
            transition: 'all 0.2s'
          }}
        >
          {res.selected ? (
            <>
              <Typography sx={{ 
                fontSize: theme.typography.fontSize.xs, 
                fontWeight: theme.typography.fontWeight.semibold, 
                lineHeight: 1, 
                color: theme.palette.primary.dark 
              }}>
                {res.name}
              </Typography>
              <Typography sx={{ 
                fontSize: `calc(${theme.typography.fontSize.xs} * 0.9)`, 
                lineHeight: 1, 
                color: theme.palette.primary.main 
              }}>
                {res.number}{res.insertionCode}
              </Typography>
            </>
          ) : (
            <Typography sx={{ 
              fontSize: `calc(${theme.typography.fontSize.xs} * 0.9)`, 
              color: theme.palette.text.disabled 
            }}>
              {res.number}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default FMOClusterDetailsDialog;